import React from 'react';
import { TrendingDown, BarChart3, Building2, MapPin } from 'lucide-react';
import { propertyData } from '../../data/mock';

const ExceptionalValueSection = () => {
  return (
    <section id="market-evidence" className="py-24 bg-gradient-to-b from-[#0d1117] to-[#161b22]" data-testid="exceptional-value-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium tracking-wide mb-4">
            Market Evidence
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-6">
            Exceptional Value in Today's Market
          </h2>
          <p className="text-lg text-white/70 font-light">
            {propertyData.marketComparison.scarcityStatement}
          </p>
        </div>

        {/* Main Comparison Card */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-amber-600/10 rounded-3xl blur-xl" />
            <div className="relative bg-gradient-to-br from-[#1a1f2e] to-[#0d1117] border-2 border-amber-500/40 rounded-3xl p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                {/* Left: Comparison */}
                <div>
                  <div className="flex items-center space-x-3 mb-6">
                    <TrendingDown className="w-8 h-8 text-green-400" />
                    <span className="text-green-400 font-semibold text-lg">BELOW MARKET</span>
                  </div>
                  
                  <p className="text-white/70 mb-6 font-light">
                    Closest active improved approximately 18–20-acre metro-market alternatives identified:
                  </p>
                  
                  <div className="bg-white/5 rounded-xl p-4 mb-4">
                    <p className="text-white/50 text-sm">Active Comparables Range</p>
                    <p className="text-2xl font-bold text-white">
                      ${(propertyData.marketComparison.comparableRange.min / 1000000).toFixed(2)}M – ${(propertyData.marketComparison.comparableRange.max / 1000000).toFixed(2)}M
                    </p>
                  </div>
                  
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                    <p className="text-amber-400/80 text-sm">William Lane Offering</p>
                    <p className="text-3xl font-bold text-amber-400">{propertyData.priceShort}</p>
                  </div>
                </div>

                {/* Right: Savings Highlight */}
                <div className="text-center md:text-left">
                  <div className="inline-block bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-2xl p-8">
                    <p className="text-green-400/80 text-sm mb-2 uppercase tracking-wide">Potential Savings</p>
                    <p className="text-4xl md:text-5xl font-bold text-white mb-4">
                      ~$1M
                    </p>
                    <p className="text-white/60 text-sm font-light">
                      {propertyData.marketComparison.savings}
                    </p>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-white/40 text-xs italic text-center">
                  {propertyData.marketComparison.disclaimer}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Price Analysis Cards */}
        <div className="grid md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {[
            { 
              label: "Price per Sq Ft of Land", 
              value: propertyData.marketComparison.priceAnalysis.pricePerSqFtLand,
              icon: MapPin,
              description: "Regional acreage trades at $9K–$65K/acre"
            },
            { 
              label: "Implied Home Cost/Sq Ft", 
              value: propertyData.marketComparison.priceAnalysis.impliedHomeCostPerSqFt,
              icon: Building2,
              description: "Area avg: $175–$320/sq ft"
            },
            { 
              label: "Infrastructure Value", 
              value: propertyData.marketComparison.priceAnalysis.infrastructureValue,
              icon: BarChart3,
              description: "Replacement cost of structures"
            },
            { 
              label: "Min. Replacement Value", 
              value: propertyData.marketComparison.priceAnalysis.replacementValue,
              icon: TrendingDown,
              description: "Land + residence + infrastructure"
            },
          ].map((item, index) => (
            <div 
              key={index}
              className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-amber-500/30 transition-all"
            >
              <item.icon className="w-5 h-5 text-amber-400 mb-3" />
              <p className="text-white/50 text-xs mb-1">{item.label}</p>
              <p className="text-xl font-bold text-white mb-2">{item.value}</p>
              <p className="text-white/40 text-xs">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExceptionalValueSection;
